import { createContext, useContext, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TOUR_STEPS } from "@/demo/tourConfig";

const CAL_URL = "https://cal.com/ss-nn-untxjg/discovery-call";

const DemoContext = createContext(null);

export function DemoProvider({ children }) {
  const [bookingOpen, setBookingOpen] = useState(false);
  const [tourActive, setTourActive] = useState(false);
  const [tourStep, setTourStep] = useState(1);
  const navigate = useNavigate();

  const openBooking = useCallback(() => {
    window.open(CAL_URL, "_blank", "noopener,noreferrer");
  }, []);
  const closeBooking = useCallback(() => setBookingOpen(false), []);

  const startTour = useCallback(() => {
    setTourActive(true);
    setTourStep(1);
    navigate(TOUR_STEPS[0].path);
  }, [navigate]);

  const stopTour = useCallback(() => setTourActive(false), []);

  const goToStep = useCallback(
    (n) => {
      const clamped = Math.max(1, Math.min(TOUR_STEPS.length, n));
      const s = TOUR_STEPS.find((x) => x.step === clamped);
      setTourStep(clamped);
      if (s) navigate(s.path);
    },
    [navigate]
  );

  const nextStep = useCallback(() => {
    if (tourStep >= TOUR_STEPS.length) {
      openBooking();
      return;
    }
    goToStep(tourStep + 1);
  }, [tourStep, goToStep, openBooking]);

  const prevStep = useCallback(() => goToStep(tourStep - 1), [tourStep, goToStep]);

  const value = {
    bookingOpen,
    openBooking,
    closeBooking,
    tourActive,
    tourStep,
    startTour,
    stopTour,
    nextStep,
    prevStep,
    totalSteps: TOUR_STEPS.length,
    currentStep: TOUR_STEPS.find((s) => s.step === tourStep),
  };

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemo() {
  const ctx = useContext(DemoContext);
  if (!ctx) throw new Error("useDemo must be used within DemoProvider");
  return ctx;
}
